import {
  generateInviteURL,
  openShareTargetPicker,
  sendInviteURL,
} from 'src/lib/sharing';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { makePayload } from 'src/lib/analytics';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import { AnalyticsProperties } from '@play-co/gcinstant';
import i18n from 'src/lib/i18n/i18n';
import { getCreativeText } from 'src/creatives/text';
import { getRandomAddFriendsV2ShareCreative } from 'src/creatives/share';
import { track } from 'src/lib/analytics/utils';
import { getRandomAddFriendsV2Asset } from 'src/replicant/chatbot/flex/invite';
import {
  createFlexContainer,
  createOpenGameAction,
  FlexCreativeAsset,
} from 'src/replicant/chatbot/flex/utils';
import { AlternativeOpenWebBuckets } from 'src/replicant/utils/payload';
import { getBuruFriendsCount } from 'src/replicant/friends/friends.getters';
import { withLoading } from 'src/lib/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';

/**
 * Friend List popup share CTAs, resolved from A/B 0470.
 *
 * Public entry points invoked by `PopupFriendsList`'s LINE / Native share
 * buttons. Each variant stamps the canonical `feature: 'friend_list'` and
 * `subFeature: 'friend_list_line_share' | 'friend_list_native_share'` onto
 * the entry payload and the sender-side `FriendRequestSend` track call so
 * receiver-side `FriendRequestReceive` / `FriendAdd` events inherit
 * Friend List attribution via the existing `SUB_FEATURE_TO_METHOD`
 * resolution. No changes to `sharing.ts` or `addFriendsV2.share.ts` are
 * needed — this module owns Friend List attribution end-to-end (mirrors
 * the `wantToInvite.share.ts` pattern from A/B 0468 V2).
 */
export async function shareAddFriendFromFriendListViaLine(): Promise<void> {
  await sendShareTargetPicker();

  track('FriendRequestSend', {
    method: 'line_share',
    friendCount: getBuruFriendsCount(user()),
    originFeature: Feature.FRIEND_LIST._,
    originSubFeature: Feature.FRIEND_LIST.LINE_SHARE,
  });
}

export async function shareAddFriendFromFriendListViaNativeShare(): Promise<void> {
  const inviteUrl = await withLoading(async () =>
    generateInviteURL({
      creativeAsset: await getRandomAddFriendsV2ShareCreative(),
      creativeText: getCreativeText('invite'),
      data: {
        feature: Feature.FRIEND_LIST._,
        $subFeature: Feature.FRIEND_LIST.NATIVE_SHARE,
        originFeature: Feature.FRIEND_LIST._,
        originSubFeature: Feature.FRIEND_LIST.NATIVE_SHARE,
      },
    }),
  );

  if (!inviteUrl?.inviteUrl) {
    await openPopupPromise('popupError', {
      message: i18n('error.general.tryAgain'),
    });
    return;
  }

  const shareMethod = await sendInviteURL({
    inviteUrl: inviteUrl.inviteUrl,
    description: i18n('friends.addFriendPopup.shareDescription'),
  });

  track('SendInviteURL', {
    url: inviteUrl.inviteUrl,
    shareMethod,
    feature: Feature.FRIEND_LIST._,
  });

  track('FriendRequestSend', {
    method: 'native_share',
    friendCount: getBuruFriendsCount(user()),
    originFeature: Feature.FRIEND_LIST._,
    originSubFeature: Feature.FRIEND_LIST.NATIVE_SHARE,
  });
}

/**
 * Send the LINE Share Target Picker with a flex message stamped with
 * Friend List feature/subFeature. The receiver decodes these from the
 * entry payload and `deriveFriendAddAttribution` resolves them.
 */
async function sendShareTargetPicker(): Promise<void> {
  const creativeAsset = getRandomCreative();
  const creativeText = getCreativeText('invite');
  const title = process.env.LINE_APP_NAME || 'ぶるぶるどーぶつ';

  const { encodedPayload, analyticsEventProperties } =
    platform.generateEntryPayload({
      ...makePayload('SHARE'),
      channel: 'SHARE' as AnalyticsProperties.Channel,
      feature: Feature.FRIEND_LIST._,
      subFeature: Feature.FRIEND_LIST.LINE_SHARE,
      $subFeature: Feature.FRIEND_LIST.LINE_SHARE,
      originFeature: Feature.FRIEND_LIST._,
      originSubFeature: Feature.FRIEND_LIST.LINE_SHARE,
      webReferrer: user().id,
      $creativeAssetID: creativeAsset.id,
      $creativeTextID: creativeText.id,
      $creativeCTA: creativeText.cta,
      creativeCTA: creativeText.cta,
    });

  const flexMessage = createFlexMessage({
    title,
    asset: creativeAsset,
    cta: creativeText.cta,
    payload: {
      ...encodedPayload,
      alternativeWebIABBucket: 'enabled_mini_app' as const,
    },
  });

  await openShareTargetPicker(flexMessage, analyticsEventProperties);
}

function createFlexMessage(args: {
  title: string;
  asset: FlexCreativeAsset;
  cta: string;
  payload: object & { alternativeWebIABBucket: AlternativeOpenWebBuckets };
}) {
  const { title, asset, cta, payload } = args;
  return createFlexContainer(title, {
    type: 'bubble',
    hero: {
      type: 'image',
      url: asset.image,
      size: 'full',
      aspectRatio: '73:41',
      aspectMode: 'cover',
      action: createOpenGameAction(cta, payload),
    },
  });
}

function getRandomCreative(): FlexCreativeAsset {
  const creative = getRandomAddFriendsV2Asset(Math.random);
  creative.image = StateObserver.replicant.getChatbotAssetUrl(creative.image);
  return creative;
}
